/* ============================================================================
 * ez-configurator-skirting.js — UI CONTROLLER for the skirting kitting island
 * (contract §1 split; the section file stays <30KB). Instance-safe: inits EVERY
 * [data-ez-cfgsk] root. Catalog (product/variant-bound) is read from each root's
 * <script data-ez-cfgsk-catalog>; ported band/stud/clamp LOGIC lives in
 * ez-engine-skirting.js (window.EZSnap, behavior-gate validated). Reset labels
 * come from data-* attrs on the root. ATC = ONE multi-line POST to /cart/add.js.
 * [2026-08-02] Re-versioned to force the storefront serving layer to pick up the ATC re-entrancy
 * guard from e924118 (that push synced to the theme but its CDN copy stalled). Logic unchanged.
 * ============================================================================ */
(function () {
  "use strict";

  // Resolve the Klaviyo public company id from the onsite embed already on the page (its loader/forms/fonts
  // URLs carry ?company_id=<id>). Portable per store — no hardcoded key. (Ported from the shade builder.)
  function klaviyoCompanyId() {
    try {
      var nodes = document.querySelectorAll('script[src*="klaviyo"],link[href*="klaviyo"]');
      for (var i = 0; i < nodes.length; i++) {
        var u = nodes[i].getAttribute('src') || nodes[i].getAttribute('href') || '';
        var m = u.match(/[?&]company_id=([A-Za-z0-9]{5,})/);
        if (m) return m[1];
      }
    } catch (e) {}
    return null;
  }
  // Fire a Klaviyo event via the CORS-free /api/track image beacon. window.klaviyo.track() does NOT deliver on
  // this store (onsite tracking runs through a third party, so the native queue never flushes — verified), so we
  // use the legacy beacon (klaviyo.js's own transport). identify() ties the browser cookie to the email.
  function klaviyoTrack(event, email, props) {
    try {
      var token = klaviyoCompanyId();
      if (!token) { if (window.console) console.warn('[ez-skirting] no Klaviyo company id found on page'); return false; }
      try { if (window.klaviyo && window.klaviyo.identify) window.klaviyo.identify({ email: email, $email: email }); } catch (e) {}
      var payload = { token: token, event: event, customer_properties: { $email: email }, properties: props || {} };
      var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
      new Image().src = 'https://a.klaviyo.com/api/track?data=' + encodeURIComponent(b64);
      return true;
    } catch (e) { if (window.console) console.warn('[ez-skirting] klaviyo track failed', e); return false; }
  }

  // Mirror the emailed quote into HubSpot (contact + quote props + browsing-stitch). Additive to the
  // Klaviyo recap above; fire-and-forget with a silent catch so it never blocks the email/UX.
  function hubspotQuoteSubmit(email, props) {
    try {
      var hutk = (document.cookie.match(/hubspotutk=([^;]+)/) || [])[1] || '';
      var summary = (props.specs || []).map(function (s) { return s.label + ': ' + s.value; }).join(' · ') || props.summary || '';
      var fields = [
        { name: 'email', value: email },
        { name: 'quoted_product', value: props.config_type || '' },
        { name: 'quoted_kit', value: props.kit || props.format || props.application || '' },
        { name: 'last_quote_total', value: (props.total != null ? String(props.total) : '') },
        { name: 'rv_make_and_model', value: props.rv || '' },
        { name: 'quote_summary', value: summary.slice(0, 600) },
        { name: 'last_quote_date', value: String(Date.now()) }
      ].filter(function (f) { return f.value !== ''; });
      fetch('https://api.hsforms.com/submissions/v3/integration/submit/7022036/6451d33b-f8cf-4cba-bf07-8f429dc6c19c', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submittedAt: Date.now(), fields: fields, context: { hutk: hutk, pageUri: window.location.href, pageName: document.title } })
      }).catch(function () {});
    } catch (e) {}
  }

  function initRoot(root) {
    var CATALOG;
    try { CATALOG = JSON.parse(root.querySelector('[data-ez-cfgsk-catalog]').textContent); }
    catch (e) { return; }
    if (!window.EZSnap || !window.EZSnap.createSkirtingEngine) return;

    var money = function (n) {
      // non-store currency (Canada market = CAD): the ONE theme formatter, window.EZ (layout/theme.liquid)
      var E = window.EZ;
      if (E && E.isBase === false && typeof E.money === 'function') return E.money(Number(n) || 0);
      return '$' + Number(n).toLocaleString('en-US', {
        minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2
      });
    };
    var COLORS = CATALOG.colors;
    var byBand = {};
    CATALOG.bands.forEach(function (b) { byBand[b.band] = b; });

    // variant-id lookups from the injected catalog (SKU -> id), so the engine can carry ids.
    var kitVarByBand = {}; // band -> { "S|T-COLOR": {id,price} }
    CATALOG.bands.forEach(function (b) {
      var map = {};
      (b.variants || []).forEach(function (v) {
        var t = (v.title || '').toLowerCase();
        var h = (t.indexOf('tall') > -1 || t.indexOf('60') > -1) ? 'T' : 'S';
        var c = 'BK';
        if (t.indexOf('beige') > -1) c = 'BG';
        else if (t.indexOf('white') > -1) c = 'WH';
        else if (t.indexOf('grey') > -1 || t.indexOf('gray') > -1) c = 'GY';
        else if (t.indexOf('black') > -1) c = 'BK';
        map[h + '-' + c] = { id: v.id, price: v.price, available: v.available };
      });
      kitVarByBand[b.band] = map;
    });
    var addonBySku = {};
    (CATALOG.addons || []).forEach(function (a) { if (a.sku) addonBySku[a.sku] = a; });

    // ── add-on PRICE LADDERS + variant ids — derived from the addon products' REAL
    //    variants (addonBySku, injected from Liquid), NEVER typed. SKU scheme (ported):
    //    clamp EZ-B-CMP-{pack} · velcro EZ-C-VEL-{ft} · bag EZ-C-SKB-{S|T}. Parse counts/sizes off
    //    the SKUs so any variant works. (Studs no longer come from an EZ-C-SNS add-on: they ship inside the
    //    kit's EZ-C-SNA-{n}-{cap}-{stud} snap packs, resolved at ATC by window.EZKitSnaps.)
    var clampPrice = {}, velPrice = {}, bagPrice, hitchPrice, hitchNcPrice, hitchBagPrice;
    var clampIdBySku = {}, velIdBySku = {}, bagIdBySku = {}, hitchIdByColor = {}, hitchNcIdByColor = {}, hitchBagId;
    // Hitch matched-extras constants — SOURCED from the standalone hitch NC engine (ez-engine-hitch.js /
    // §ez-configurator-hitch.liquid): the NC hitch enclosure needs recClampRawNc=13 raw clamps (→ a 25-pack on
    // its own) and ships snapCount=100 (→ one 100-count EZ-C-SNA snap pack). Its 74″ storage bag is
    // EZ-C-SKB-X. Prices/ids are read from the injected catalog (below), never typed — only these COUNTS are logic.
    var HITCH_CLAMP_RAW = 13, HITCH_SNAP_COUNT = 100;
    Object.keys(addonBySku).forEach(function (sku) {
      var a = addonBySku[sku], m;
      if ((m = /^EZ-B-CMP-(\d+)$/.exec(sku))) { clampPrice[+m[1]] = a.price; clampIdBySku[sku] = a.id; }
      else if ((m = /^EZ-C-VEL-(\d+)$/.exec(sku))) { velPrice[+m[1]] = a.price; velIdBySku[sku] = a.id; }
      else if ((m = /^EZ-C-SKB-([ST])$/.exec(sku))) { if (bagPrice == null) bagPrice = a.price; bagIdBySku[sku] = a.id; }
      else if (sku === 'EZ-C-SKB-X') { hitchBagPrice = a.price; hitchBagId = a.id; }   // hitch 74″ bag (matched-extras)
      // 5th-wheel hitch enclosure upsell. A side-skirting customer already seals the back of the hitch area with
      // their skirting, so this upsell adds the NO-CONNECTOR variant (EZ-B-HIT-NC-{color}, $50 less than the
      // standalone With-connector kit). Match NC BEFORE the WC pattern; mutually exclusive regexes; both
      // parsed so the maps stay complete even if only one connector side is stocked.
      else if ((m = /^EZ-B-HIT-NC-([A-Z]+)$/.exec(sku))) { if (hitchNcPrice == null) hitchNcPrice = a.price; hitchNcIdByColor[m[1]] = a.id; }
      else if ((m = /^EZ-B-HIT-WC-([A-Z]+)$/.exec(sku))) { if (hitchPrice == null) hitchPrice = a.price; hitchIdByColor[m[1]] = a.id; }
    });
    function hitchAvailable() { return Object.keys(hitchNcIdByColor).length > 0; }

    // Build the engine — data injected, LOGIC ported (behavior-gate validated).
    var engine = window.EZSnap.createSkirtingEngine({
      bands: CATALOG.bands,
      colors: COLORS,
      clampPrice: clampPrice,
      velPrice: velPrice,
      bagPrice: bagPrice,
      variantId: {
        kit: function (band, h, c) { var m = kitVarByBand[band.band] || {}; return (m[h + '-' + c] || {}).id; },
        clamp: function (pack) { return clampIdBySku['EZ-B-CMP-' + pack]; },
        vel: function (ft) { return velIdBySku['EZ-C-VEL-' + ft]; },
        bag: function (h) { return bagIdBySku['EZ-C-SKB-' + (h === 'T' ? 'T' : 'S')]; }
      }
    });

    // ── state ──
    // rvType/slides refine the material need (Troy's fifth-wheel + slide-out ask). slides == null means
    // "use the length band's assumed count" (the display shows that assumed number until the customer sets one).
    // rvType drives band selection with a SAFE default of 'trailer' (no nose offset → most material).
    // rvTypeSet tracks whether the CUSTOMER confirmed the type: the YMM lookup can't (fitment has no type),
    // so we record type/slides as data only once explicitly chosen — an assumed default is not stated fact.
    var st = { band: null, height: null, color: null, open: 1, len: null, rvType: 'trailer', rvTypeSet: false, slides: null,
               // studColor: null = studs match the kit color (the included default); a code = the shopper's
               // different stud pick (one flat stud color upgrade per build). Caps always match the kit color.
               clampsOn: true, clampPack: null, studColor: null,
               velcroOn: false, velFt: 10, bagOn: false, hitchOn: false,
               // Hitch matched-extras (nested under the hitch upsell). Clamps default on (pooled); bag default off
               // (a 2nd $99 bag is opt-in). The hitch's snap pack is always added, studs in the build's stud color.
               // Only meaningful when hitchOn.
               hitchClampOn: true, hitchBagOn: false,
               reviewedBand: null, lookupMeta: null };

    // ── element refs ──
    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
    var form = $('[data-ez-cfgsk-form]');
    var lenInput = $('[data-ez-cfgsk-len]');
    var priceVal = $('[data-ez-cfgsk-priceval]');
    var priceLabel = $('[data-ez-cfgsk-pricelabel]');
    var ledger = $('[data-ez-cfgsk-ledger]');
    var rigBox = $('[data-ez-cfgsk-rig]');
    var slideCountEl = $('[data-ez-cfgsk-slidecount]');
    var rigNote = $('[data-ez-cfgsk-rignote]');
    // ── method: the two Step-1 methods (RV lookup / manual length) are an either/or; committing to
    //    one collapses the other to a one-tap switch link. 'none' = both shown (customer hasn't chosen). ──
    var methodHost = $('[data-ez-cfgsk-methodhost]');
    var method = 'none';
    function setMethod(m) { method = m; if (methodHost) methodHost.setAttribute('data-ez-cfgsk-method', m); }

    // ── derived ──
    // Type-confirmed gate: a resolved band is only a real recommendation once the customer states RV type
    // (or it came from the RV lookup, which is its own confident signal). A manual length alone defaults to
    // 'trailer', which OVER-sizes a fifth-wheel (Troy: a 43 ft 5er was shown the 150 ft trailer kit before
    // ever picking type). Holding s1done + the resolve/price until type is set makes the recommendation wait
    // for the one input that most changes it — matching Troy's "ask RV type before recommending the size".
    function typeKnown() { return st.rvTypeSet || !!st.lookupMeta; }
    function s1done() { return !!(st.band && !st.band.oversize && typeKnown()); }
    function coreResolved() { return s1done() && !!st.height && !!st.color; }
    function kitPrice() { return s1done() ? (st.height === 'T' ? st.band.pT : st.band.pS) : 0; }
    function kitSku() { return coreResolved() ? engine.kitSku(st.band, st.height, st.color) : null; }
    function addonSku(key) {
      var m = { clamp: 'EZ-B-CMP-', vel: 'EZ-C-VEL-', bag: 'EZ-C-SKB-' };
      return m[key];
    }

    // ── stud color (snap packs) ──
    // The studs ship INSIDE the kit's snap packs (EZ-C-SNA-{n}-{cap}-{stud}); caps always match the skirting.
    // Studs default to the skirting color (included). A different stud color is ONE flat upgrade per build
    // (EZKitSnaps.UPGRADE, $60), whatever the snap count, kit qty or nested hitch — the server prices it on the
    // snap lines; here it is display only (a studFee line that is never posted).
    function studCode() { return st.studColor || st.color; }
    function studDiff() { return !!(st.color && st.studColor && st.studColor !== st.color); }
    // "275 Black snaps", or "275 snaps (Black caps, White studs)" with a different stud color
    function snapsPhrase() {
      return studDiff()
        ? st.band.snaps + ' snaps (' + engine.colorName(st.color) + ' caps, ' + engine.colorName(st.studColor) + ' studs)'
        : st.band.snaps + ' ' + engine.colorName(st.color) + ' snaps';
    }
    function upgradeUsd() { var K = window.EZKitSnaps; return (K && Number(K.UPGRADE)) || 60; }
    // The upgrade is a flat USD amount. A non-USD shopper (Canada market) sees it labeled "US$60" (the typed-USD
    // rule, window.EZ.usd) and it stays OUT of the presentment-currency running total rather than mixing currencies.
    function isBaseCcy() { var E = window.EZ; return !(E && E.isBase === false); }
    function upgradeAmt() { return isBaseCcy() ? upgradeUsd() : 0; }
    function upgradeText() {
      var E = window.EZ;
      return (!isBaseCcy() && E && typeof E.usd === 'function') ? E.usd('$' + upgradeUsd()) : money(upgradeUsd());
    }
    // A line's display amount: snap packs are included; the stud upgrade shows its flat fee label.
    function lineAmtText(l) {
      if (l.kitSnaps) return 'Included';
      if (l.studFee) return upgradeText();
      return money((l.price || 0) * l.qty);
    }

    // ── clamp pooling (hitch matched-extras) ──
    // The skirting's RAW clamp need is material_ft ÷ 2 (clamps every 24″). VERIFIED: packFor(material/2)
    // reproduces the clamp_pack metafield on all 7 bands, so this recovers the raw count the data model
    // pre-rounds away — a sourced derivation, not a fabricated number. Pooling sizes ONE pack to skirt-raw +
    // the hitch's 13, so the hitch clamps ride in the skirting pack's slack (free) or tip it one size (the
    // bump) — never a second pack. When the skirt has no clamps to pool into, the hitch buys its own 25-pack.
    function packFor(raw) { var P = [25, 50, 75, 100]; for (var i = 0; i < P.length; i++) if (P[i] >= raw) return P[i]; return 100; }
    function skirtClampRaw() { return st.band ? Math.ceil(st.band.material / 2) : 0; }
    function hitchClampsPooling() { return st.hitchOn && st.hitchClampOn && st.clampsOn; }  // pooled (vs own pack / off)
    // The skirting clamp pack the build actually buys: the customer's manual override wins; otherwise the
    // POOLED recommendation (which, with the hitch off, equals packFor(skirt-raw) === the band's clamp_pack).
    function pooledClampPack() { return packFor(skirtClampRaw() + (hitchClampsPooling() ? HITCH_CLAMP_RAW : 0)); }
    function effClampPack() { return st.clampPack || pooledClampPack(); }
    // The "if added" cost of the hitch's clamps — 0 when they fit the skirting pack's slack, one pack-step when
    // they tip it, or a whole 25-pack when the skirt isn't clamped. DISPLAY-ONLY (the nested clamp row's price):
    // the real total flows through effClampPack() bumping the single skirting clamp line, never a 2nd line — so
    // this is computed "as if on" regardless of the toggle, to show what turning it on costs.
    function hitchClampDelta() {
      if (!st.hitchOn) return 0;
      if (!st.clampsOn) return clampPrice[packFor(HITCH_CLAMP_RAW)] || 0;   // no skirt pool → its own 25-pack
      var base = st.clampPack || packFor(skirtClampRaw());
      var pooled = st.clampPack || packFor(skirtClampRaw() + HITCH_CLAMP_RAW);
      return (clampPrice[pooled] || 0) - (clampPrice[base] || 0);
    }

    // ── RV fit: type + slide-outs drive the material need; the length band is the floor (never a
    //    smaller kit than the length implies), bumped up only when slides/length demand it. ──
    function currentSlides() { return st.slides == null ? engine.assumedSlides(floorLen()) : st.slides; }
    function resolveBand() { st.band = engine.bandForBuild({ len: st.len, type: st.rvType, slides: st.slides }); }
    // The FLOOR length the kit sizes from — for a fifth-wheel that's the BODY length (len − nose offset),
    // matching bandForBuild. The display uses this so its reference band (the "sized up from…" note and the
    // slide-stepper visibility gate) is the actual floor, not the full length — which for a FW sits a band
    // ABOVE the resolved kit, making the old "sized up" note read backwards.
    function floorLen() { return (st.rvType === 'fifth_wheel' && st.len != null) ? Math.max(0, st.len - engine.fit.noseOffset) : st.len; }
    // Flat-front rigs (travel trailer, motorhome) skirt the full length; only a fifth-wheel subtracts the gooseneck.
    // Motorhome sizes IDENTICALLY to a trailer (engine: type !== 'fifth_wheel' → no nose offset); it's a distinct
    // LABEL so a Class-A owner sees themselves in the picker instead of bouncing (Troy: "lots of these" inquiries).
    function rvTypeLabel() {
      return st.rvType === 'fifth_wheel' ? 'Fifth-wheel'
        : st.rvType === 'class_a' ? 'Class A Motorhome'
        : st.rvType === 'class_b' ? 'Class B Camper Van'
        : st.rvType === 'class_c' ? 'Class C Motorhome'
        : st.rvType === 'park_model' ? 'Park Model'
        : 'Travel trailer';
    }
    // Slide-out DISPLAY text. Once the customer states a count, confirm THEIR number ("Covers your 3
    // slide-outs") — never the resolved band's assumed metafield count, which differs after a size-up:
    // a 21ft/3-slide rig bumps to the 26–30 band whose metafield assumes 2, so "up to 2" contradicted "3".
    function slideDisplay() {
      if (st.slides != null) return st.slides === 0 ? 'For no slide-outs' : ('Covers your ' + st.slides + ' slide-out' + (st.slides > 1 ? 's' : ''));
      return st.band ? engine.slideText(st.band) : '';
    }

    // ── Supabase capture: log every resolved RV so the "not in our list / how many slides" gaps become
    //    real data (ezsnap-rv-capture edge fn). Debounced + deduped by signature so one distinct build =
    //    one row, not one per keystroke. Fire-and-forget; never blocks the UI. ──
    function sessionId() {
      try { if (!window.__ezRvSid) window.__ezRvSid = 'rv' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8); return window.__ezRvSid; } catch (e) { return null; }
    }
    var captureTimer, captureSeen = Object.create(null);
    function captureSig() {
      var lm = st.lookupMeta;
      // Match the payload's full RV identity so A→B→A revisits and same-model-different-year rigs are
      // each distinguished (the old scalar remembered only the LAST sig, so A→B→A re-inserted A).
      return [st.len, (st.rvTypeSet ? st.rvType : ''), (st.slides == null ? '' : st.slides), st.band && st.band.band,
              lm && lm.year, lm && lm.make, lm && lm.model, lm && lm.fp].join('|');
    }
    function scheduleCapture() {
      if (!st.band || st.band.oversize) return;
      if (!(window.EZSnap && window.EZSnap.rvCapture)) return;
      clearTimeout(captureTimer);
      captureTimer = setTimeout(function () {
        var sig = captureSig();
        if (captureSeen[sig]) return;   // dedup against EVERY signature seen this session, not just the last
        captureSeen[sig] = true;
        var lm = st.lookupMeta;
        window.EZSnap.rvCapture({
          source: lm ? (lm.custom ? 'other' : 'lookup') : 'manual',   // 'other' = customer-entered rig not in our lookup
          found_in_db: lm ? (lm.custom ? false : true) : null,        // false → feeds the missing-model backfill queue
          rv_year: lm && lm.year ? Number(lm.year) : null,
          rv_make: lm ? lm.make : null,
          rv_model: lm ? lm.model : null,
          rv_floorplan: lm ? lm.fp : null,
          rv_type: st.rvTypeSet ? st.rvType : null,               // record type only when the customer stated it
          slide_outs: (st.slides == null ? null : st.slides),     // record slides only when explicitly entered
          entered_length_ft: st.len,
          db_length_ft: (lm && lm.ft10 != null) ? (lm.ft10 / 10) : null,
          resolved_band: st.band.band,
          resolved_material_ft: st.band.material,
          page_handle: (location.pathname || '').replace(/^\/+|\/+$/g, '').slice(0, 120),
          session_id: sessionId()
        });
      }, 900);
    }

    function lines() {
      if (!coreResolved()) return [];
      var out = engine.buildKitLines({
        band: st.band, height: st.height, color: st.color, slideLabel: slideDisplay(),
        clampsOn: st.clampsOn, clampPack: effClampPack(),   // pooled: bumps to cover the hitch's clamps when on
        studColor: studCode(),                              // snap packs: caps = kit color, studs = this
        velcroOn: st.velcroOn, velFt: st.velFt, bagOn: st.bagOn
      });
      // Stud color upgrade: ONE display line per build, right after the skirting's snap packs. Never posted —
      // the server charges it on the snap lines; it is here so the review, totals and emailed recap show it.
      if (studDiff()) {
        var at = 0;
        out.forEach(function (l, i) { if (l.kitSnaps) at = i + 1; });
        out.splice(at || out.length, 0, {
          sku: '', title: 'Stud color upgrade', opts: engine.colorName(st.studColor) + ' studs · one charge for the whole build',
          qty: 1, price: upgradeAmt(), studFee: true
        });
      }
      // 5th-wheel hitch-enclosure upsell (restored port-gap — see hitch-audit/AUDIT-5thwheel-hitch.md). One
      // color-matched EZ-B-HIT-NC (no-connector) kit line PLUS its snap pack and matched extras: the 74″ hitch
      // bag, and — only when the skirt itself isn't clamped — its own clamp pack. Otherwise the hitch's clamps
      // POOL into the skirting clamp line above (effClampPack already bumped it), so there is NO second clamp
      // line. All hitch lines carry the same _build as the skirting → the cart groups one order.
      if (st.hitchOn && hitchAvailable()) {
        var hc = hitchNcIdByColor[st.color] ? st.color : (hitchNcIdByColor.BK ? 'BK' : Object.keys(hitchNcIdByColor)[0]);
        var hid = hitchNcIdByColor[hc];
        if (hid) {
          out.push({ sku: 'EZ-B-HIT-NC-' + hc, title: '5th-Wheel Hitch Enclosure Kit', opts: engine.colorName(hc) + ' · no connector · closes the raised front nose', qty: 1, price: hitchNcPrice, variantId: hid });
          // the hitch's own snap pack (its snapCount=100): caps = the hitch panel color, studs = the build's stud
          // color. Included; the build's single stud upgrade (above) already covers a different stud color.
          var hstud = studCode();
          out.push({ sku: 'EZ-C-SNA-' + HITCH_SNAP_COUNT + '-' + hc + '-' + hstud, title: 'Hitch Snap Pack', opts: HITCH_SNAP_COUNT + '-count · ' + engine.colorName(hc) + ' caps · ' + engine.colorName(hstud) + ' studs', qty: 1, price: 0, kitSnaps: true, packN: HITCH_SNAP_COUNT, cap: hc, stud: hstud, hitch: true });
        }
        // clamps: pooled into the skirting pack above — EXCEPT when the skirt has no clamps, then the hitch buys
        // its own pack (13 raw → a 25-pack), exactly like the standalone hitch page.
        if (st.hitchClampOn && !st.clampsOn) {
          var hp = packFor(HITCH_CLAMP_RAW);
          var hcid = clampIdBySku['EZ-B-CMP-' + hp];
          if (hcid) out.push({ sku: 'EZ-B-CMP-' + hp, title: 'Pipe Clamp Pack — hitch', opts: hp + '-count · for the hitch enclosure', qty: 1, price: clampPrice[hp], variantId: hcid, hitch: true });
        }
        // 74″ hitch storage bag (distinct SKU from the skirt bag; can't pool)
        if (st.hitchBagOn && hitchBagId) out.push({ sku: 'EZ-C-SKB-X', title: 'Storage Bag — hitch', opts: 'Hitch 74″', qty: 1, price: hitchBagPrice, variantId: hitchBagId, hitch: true });
      }
      return out;
    }
    function total() { return lines().reduce(function (t, l) { return t + (l.price || 0) * l.qty; }, 0); }
    function addOnTotal() {
      return lines().reduce(function (t, l) {
        // the kit and its stud color choice are not add-ons (the stud upgrade is a main choice, not an extra)
        return (l.studFee || (l.sku && l.sku.indexOf('EZ-B-SKT') === 0)) ? t : t + (l.price || 0) * l.qty;
      }, 0);
    }

    // ── swatches (step 3) ──
    var swWrap = $('[data-ez-cfgsk-swatches]');
    COLORS.forEach(function (c) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ez-cfgsk__swatch';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-label', c.name + ' skirting');
      b.setAttribute('aria-checked', 'false');
      b.dataset.code = c.code;
      b.innerHTML = '<span class="ez-cfgsk__swatch-dot" style="background:' + c.sw + ';border-color:' + c.br + ';"></span><span class="ez-cfgsk__swatch-name">' + c.name + '</span>';
      b.addEventListener('click', function () { pickColor(c.code); });
      swWrap.appendChild(b);
    });

    // ── stud color (step 3, a MAIN choice right after the skirting color) ──
    // Injected under the color help line, before the step's Continue button (no section markup change).
    // Hidden until a skirting color is picked: the default stud color IS the skirting color.
    var studHost = document.createElement('div');
    studHost.setAttribute('data-ez-cfgsk-studpick', '');
    studHost.style.margin = '4px 0 18px';
    studHost.hidden = true;
    (function () {
      var c3btn = $('[data-ez-cfgsk-cont3]');
      if (c3btn && c3btn.parentNode) c3btn.parentNode.insertBefore(studHost, c3btn);
      else if (swWrap && swWrap.parentNode) swWrap.parentNode.appendChild(studHost);
    })();
    function renderStudPick() {
      if (!st.color) { studHost.hidden = true; studHost.innerHTML = ''; return; }
      studHost.hidden = false;
      var upTxt = '+' + upgradeText();
      var kitName = engine.colorName(st.color);
      var nSnaps = (st.band && st.band.snaps) ? st.band.snaps + ' ' : '';
      var note = studDiff()
        ? '<span class="ez-stud-pill is-upgrade">' + upTxt + '</span> <b>Stud color upgrade: ' + engine.colorName(st.studColor) + ' studs.</b> ' +
          'One flat ' + upTxt + ' for the whole build' + (st.hitchOn ? ' (hitch enclosure included)' : '') + ', not per snap. ' +
          'Caps stay ' + kitName + ' to match the skirting. Pick ' + kitName + ' again for included studs.'
        : '<span class="ez-stud-pill is-included">Included</span> <b>' + kitName + ' studs</b> match your ' + kitName + ' skirting. No extra cost. ' +
          'The studs stay on your RV after the skirt comes off: want them to blend with your RV body or trim instead? Pick that color: one flat ' + upTxt + ' for the whole build, not per snap.';
      studHost.innerHTML =
        '<span class="ez-cfgsk__field-label">Stud color</span>' +
        '<div class="ez-cfgsk__swatches" role="radiogroup" aria-label="Stud color">' + COLORS.map(function (c) {
          var isKit = c.code === st.color;
          var on = c.code === studCode();
          return '<button type="button" class="ez-cfgsk__swatch' + (on ? ' is-selected' : '') + '" role="radio" aria-checked="' + (on ? 'true' : 'false') + '"' +
            ' data-studcolor="' + c.code + '" aria-label="' + c.name + ' studs, ' + (isKit ? 'matches your skirting, included' : upTxt + ' stud color upgrade') + '">' +
            '<span class="ez-cfgsk__swatch-dot" style="background:' + c.sw + ';border-color:' + c.br + ';"></span>' +
            '<span class="ez-cfgsk__swatch-name">' + c.name + '<br><small class="ez-cfgsk__stud-tag' + (isKit ? '' : ' is-upgrade') + '">' + (isKit ? 'Included' : upTxt) + '</small></span></button>';
        }).join('') + '</div>' +
        '<p class="ez-cfgsk__field-note">' + note + '</p>';
      studHost.querySelectorAll('[data-studcolor]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var c = btn.dataset.studcolor;
          st.studColor = (c === st.color) ? null : c;   // picking the skirting color = back to the included match
          renderAll();
          var again = studHost.querySelector('[data-studcolor="' + c + '"]');   // re-rendered: keep keyboard focus
          if (again) again.focus();
        });
      });
    }

    // ── step open/lock machinery ──
    function stepEl(n) { return root.querySelector('[data-ez-cfgsk-step="' + n + '"]'); }
    // Bring a just-opened step's header under the fixed site header. Advancing re-renders (the prior
    // step collapses, the review step expands), shifting layout under a fixed scrollY — without this the
    // viewport can land PAST the configurator in the next page section (reported on "Review my order →").
    // Only user-driven advances pass scroll:true; load/restore + every re-render call setOpen without it.
    function scrollStepIntoView(n) {
      var el = stepEl(n);
      if (!el) return;
      // Wait for the re-render/collapse to fully settle before measuring, THEN anchor the step's top
      // 80px down (the .ez-cfgsk__anchor header offset). Measuring too early — before the collapsed
      // step's height is removed — is what let the scroll overshoot past the step. Double rAF = layout
      // committed; smooth-scroll to the settled position lands on the step header, not below it.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var y = el.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        });
      });
    }
    function setOpen(n, scroll) {
      st.open = n;
      $$('[data-ez-cfgsk-step]').forEach(function (el) {
        var num = Number(el.dataset.ezCfgskStep);
        el.classList.toggle('is-open', num === n);
        // peek = the step right after the open one (demo Section: isPeek = !isOpen && n === open+1),
        // regardless of locked — the locked peek shows the ghosted preview + ink title.
        el.classList.toggle('is-peek', num === n + 1 && !isDone(num));
      });
      if (scroll) scrollStepIntoView(n);
    }
    function isDone(n) {
      if (n === 1) return s1done();
      if (n === 2) return !!st.height;
      if (n === 3) return !!st.color;
      if (n === 4) return st.open > 4 && coreResolved();
      return false;
    }
    function setLocked(n, locked) {
      var el = stepEl(n);
      if (el) el.classList.toggle('is-locked', locked);
    }

    // ── render ──
    function renderRail() {
      var resolved = coreResolved();
      if (priceVal) priceVal.textContent = resolved ? money(total()) : '—';
      if (priceLabel) priceLabel.textContent = resolved
        ? (addOnTotal() > 0 ? 'Your kit + add-ons' : 'Your kit') : (root.dataset.priceLabel || 'Your kit price');
      // Sticky build ledger — a typeset label→value spec list that grows as the build resolves.
      // The Color row is component-aware: the kit color dresses the skirting, the snap CAPS (on the
      // skirt) AND the snap STUDS (on the RV) — so name all three. A different stud color (the Stud color
      // pick) is the only thing that splits them (studs go off-color to match the rig), so surface that split.
      if (ledger) {
        if (!s1done()) {
          ledger.className = 'ez-cfgsk__ledger is-empty';
          ledger.innerHTML = '<span class="ez-cfgsk__ledger-empty">' + (root.dataset.cardSub || 'Configure yours below') + '</span>';
        } else {
          ledger.className = 'ez-cfgsk__ledger';
          var colorVal;
          if (st.color) {
            var kitC = engine.colorName(st.color);
            colorVal = studDiff()
              ? kitC + '<span class="ez-cfgsk__lnote"> skirting &amp; caps</span> · ' + engine.colorName(st.studColor) + '<span class="ez-cfgsk__lnote"> studs</span>'
              : kitC + '<span class="ez-cfgsk__lnote"> · skirting, caps &amp; studs</span>';
          }
          var rows = [['RV Length', (st.len != null ? st.len + ' ft' : st.band.label), '']];
          if (st.height) rows.push(['Height', st.height === 'T' ? '60″ Tall' : '46″ Standard', '']);
          if (colorVal) rows.push(['Color', colorVal, ' ez-cfgsk__stat--wide']);
          ledger.innerHTML = rows.map(function (r) {
            return '<div class="ez-cfgsk__stat' + r[2] + '"><dt class="ez-cfgsk__lkey">' + r[0] + '</dt><dd class="ez-cfgsk__lval">' + r[1] + '</dd></div>';
          }).join('');
        }
      }
      // Dynamic material length in the product title — the figure returning ("N-foot kit") customers anchor on.
      var cardmat = $('[data-ez-cfgsk-cardmat]');
      if (cardmat) cardmat.innerHTML = (s1done() && st.band.material) ? ('<b>' + st.band.material + ' ft</b> of Diamond Weave&trade; Skirting Fabric') : '';
    }

    // The "Refine for your rig" controls (type toggle + slide-out stepper) appear once a length resolves.
    // The stepper/toggle markup is static (stays in the DOM across renders — no focus loss); this only
    // syncs the active state + the slide count number.
    function renderRig() {
      if (!rigBox) return;
      // Gate on the LENGTH band, not the resolved band — so a slide-induced oversize doesn't hide the
      // stepper and strand the customer: they must be able to step slides back down to recover.
      var lb = st.len != null ? engine.bandForLength(floorLen()) : null;
      var show = !!(lb && !lb.oversize);
      rigBox.hidden = !show;
      if (!show) return;
      if (slideCountEl) slideCountEl.textContent = String(currentSlides());
      $$('[data-ez-cfgsk-type]').forEach(function (b) {
        // Don't highlight a type until the customer actually picks one — otherwise the default 'trailer'
        // button reads as already-selected and undercuts the "pick your RV type" prompt in the resolve box.
        var on = st.rvTypeSet && b.getAttribute('data-ez-cfgsk-type') === st.rvType;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (rigNote) {
        rigNote.innerHTML = st.rvType === 'fifth_wheel'
          ? 'Fifth-wheels skirt from behind the gooseneck. The raised nose is covered by the separate hitch-enclosure kit, so this measures the main body.'
          : st.rvType === 'class_a'
          ? 'Class A: bus-style, flat vertical front. Skirts the full length of the flat sides like a travel trailer. No gooseneck, so no hitch-enclosure kit.'
          : st.rvType === 'class_b'
          ? 'Class B: built inside a van, no bed over the cab. Skirts the full length. No gooseneck, so no hitch-enclosure kit.'
          : st.rvType === 'class_c'
          ? 'Class C: a van cab with a bunk over the cab. Skirts the flat sides of the coach. No gooseneck, so no hitch-enclosure kit.'
          : st.rvType === 'park_model'
          ? 'Park model: skirts the full length of the flat sides. No gooseneck, so no hitch-enclosure kit.'
          : 'Travel trailer: flat front, skirts the full length of the sides.';
      }
    }

    function renderStepChips() {
      var c1 = $('[data-ez-cfgsk-chip="1"]');
      var c2 = $('[data-ez-cfgsk-chip="2"]');
      var c3 = $('[data-ez-cfgsk-chip="3"]');
      if (c1) c1.textContent = s1done() ? (st.band.label + ' · ' + slideDisplay()) : '';
      if (c2) c2.textContent = st.height ? (st.height === 'T' ? 'Tall 60″' : 'Standard 46″') : '';
      if (c3) c3.textContent = st.color ? engine.colorName(st.color) + (studDiff() ? ' · ' + engine.colorName(st.studColor) + ' studs' : '') : '';
      [1, 2, 3, 4].forEach(function (n) {
        var num = $('[data-ez-cfgsk-num="' + n + '"]');
        if (num) num.textContent = isDone(n) ? '✓' : String(n);
        var el = stepEl(n);
        if (el) el.classList.toggle('is-done', isDone(n));
      });
    }

    function renderResolve() {
      var box = $('[data-ez-cfgsk-resolve]');
      if (!box) return;
      if (!st.band) { box.hidden = true; box.innerHTML = ''; return; }
      box.hidden = false;
      // Hold the confident size/price until the customer confirms RV type (the "Refine for your rig" toggle
      // sits directly above). Without this a fifth-wheel owner who only types a length lands on the trailer-
      // sized kit (Troy's 43 ft → 150 ft). Once they pick a type, renderAll re-runs and the price resolves.
      if (!typeKnown()) {
        box.className = 'ez-cfgsk__resolve';
        box.innerHTML = '<div class="ez-cfgsk__resolve-line">Pick your <b>RV type</b> above (and set any slide-outs) so we size the kit right. Fifth-wheels skirt from behind the gooseneck, so they size to the body length behind the hitch.</div>';
        return;
      }
      if (st.band.oversize) {
        box.className = 'ez-cfgsk__resolve ez-cfgsk__resolve--over';
        // Two distinct oversize causes: an actual >50 ft length, vs a valid length whose EXPLICIT slide
        // count needs more than the largest stock roll. Never tell a 45 ft rig it's "over 50 ft".
        var overSlides = st.band.overBy === 'slides';
        var scO = currentSlides();
        var overH = overSlides ? 'That’s a lot of skirting — let’s build it custom' : 'Over 50 ft — let’s build it custom';
        var overP = overSlides
          ? 'Your ' + (st.len != null ? st.len + ' ft rig ' : 'rig ') + 'with ' + scO + ' slide-out' + (scO > 1 ? 's' : '') + ' needs more skirting than our largest stock kit covers. Our team will spec it for you — or lower the slide-out count if that isn’t right.'
          : 'RVs longer than 50 ft are outside our stock kit range. Our team will spec it for you.';
        box.innerHTML = '<div class="ez-cfgsk__resolve-h">' + overH + '</div>' +
          '<p>' + overP + '</p>' +
          (root.dataset.proLink ? '<a class="ez-cfgsk__btn ez-cfgsk__btn--ink ez-cfgsk__btn--sm" href="' + root.dataset.proLink + '">Contact sales →</a>' : '');
        return;
      }
      // resolved from the RV lookup → present as a confident sized-for-you RESULT (copper-tinted, RV eyebrow,
      // Adjust escape hatch). Typed manually → the plain resolve line.
      box.className = 'ez-cfgsk__resolve' + (st.lookupMeta ? ' ez-cfgsk__resolve--over' : '');
      var lenTxt = st.len != null ? (st.len + ' ft') : 'Based on your length';
      var h = '';
      if (st.lookupMeta) {
        var m = st.lookupMeta;
        var rv = rvLabel(m);
        h += '<div class="ez-cfgsk__resolve-rv">' + rv + '</div>';
      }
      h += '<div class="ez-cfgsk__resolve-line"><b>' + lenTxt + '</b> → fits the <b>' + st.band.label +
        '</b> kit. ' + slideDisplay() + '.</div>';
      // Surface the included material length at the decision point — returning customers who shopped the old
      // "N-foot" kits anchor on skirting feet, not RV length, so show it here (not only in the Step-4 recap).
      if (st.band.material) h += '<div class="ez-cfgsk__resolve-mat">Includes <b>' + st.band.material + ' ft</b> of Diamond Weave&trade; Skirting Fabric — enough to wrap your rig, with room to trim.</div>';
      if (st.band.under) h += '<div class="ez-cfgsk__resolve-note">Below our smallest band — this kit is sized for a 20 ft rig, so you’ll have extra to trim.</div>';
      // Slide-out feedback. A bump only ever happens now when the customer EXPLICITLY set more slides than
      // the length band assumes, so the "sized up" note always names a real, customer-entered count — never
      // an assumed default. When they set a count the length band already covers, reassure instead of bumping.
      var lenBand = st.len != null ? engine.bandForLength(floorLen()) : null;
      if (lenBand && !lenBand.oversize && !lenBand.under) {
        if (st.band.band !== lenBand.band) {
          h += '<div class="ez-cfgsk__resolve-note ez-cfgsk__resolve-note--up">Sized up from the ' + lenBand.label + ' kit so it covers your ' + st.slides + ' slide-out' + (st.slides > 1 ? 's' : '') + '.</div>';
        } else if (st.slides != null) {
          var sTxt = st.slides === 0 ? 'no slide-outs' : (st.slides + ' slide-out' + (st.slides === 1 ? '' : 's'));
          h += '<div class="ez-cfgsk__resolve-note">Your ' + lenBand.label + ' kit already covers ' + sTxt + '.</div>';
        }
      }
      h += '<button type="button" class="ez-cfgsk__btn ez-cfgsk__btn--sm" data-ez-cfgsk-tostep2>' +
        (st.reviewedBand === st.band.band ? 'Back to review →' : 'Continue to height →') + '</button>';
      if (st.lookupMeta && !st.lookupMeta.custom) h += '<button type="button" class="ez-cfgsk__adjust" data-ez-cfgsk-adjust>Adjust length →</button>';
      box.innerHTML = h;
      var b2 = box.querySelector('[data-ez-cfgsk-tostep2]');
      if (b2) b2.addEventListener('click', function () {
        if (st.reviewedBand === st.band.band) setOpen(5, true); else setOpen(2, true);
      });
      // Adjust → open the manual field (keeping the calc'd length, now editable)
      var adj = box.querySelector('[data-ez-cfgsk-adjust]');
      if (adj) adj.addEventListener('click', function () { setMethod('manual'); if (lenInput) lenInput.focus(); });
    }

    function renderHeightPrices() {
      var pS = s1done() ? st.band.pS : 0;
      var pT = s1done() ? st.band.pT : 0;
      var eS = $('[data-ez-cfgsk-hprice="S"]');
      var eT = $('[data-ez-cfgsk-hprice="T"]');
      if (eS) eS.textContent = s1done() ? money(pS) : '—';
      if (eT) eT.textContent = s1done() ? money(pT) : '—';
      $$('[data-ez-cfgsk-height]').forEach(function (b) {
        b.classList.toggle('is-selected', b.dataset.ezCfgskHeight === st.height);
      });
    }

    function renderSwatches() {
      // scoped to the SKIRTING color row (the stud color picker reuses the swatch styling; renderStudPick owns it)
      Array.prototype.slice.call(swWrap.querySelectorAll('.ez-cfgsk__swatch')).forEach(function (b) {
        var on = b.dataset.code === st.color;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    // the step-4 "ships complete" note names the actual cap + stud colors when the studs differ
    var extrasHelp = $('[data-ez-cfgsk-extras-help]');
    var extrasHelpOrig = extrasHelp ? extrasHelp.innerHTML : '';
    function renderStep4() {
      if (!coreResolved()) return;
      if (extrasHelp) {
        extrasHelp.innerHTML = studDiff()
          ? extrasHelpOrig.replace(/color-matched snaps (&amp;|&) studs/, 'snaps with ' + engine.colorName(st.color) + ' caps and ' + engine.colorName(st.studColor) + ' studs')
          : extrasHelpOrig;
      }
      var recap = $('[data-ez-cfgsk-recap]');
      var cName = engine.colorName(st.color);
      var hLabel = st.height === 'T' ? '60″ Tall' : '46″ Standard';
      if (recap) {
        recap.innerHTML =
          '<div class="ez-cfgsk__recap-head"><span class="ez-cfgsk__recap-name">EZ-Snap Skirting Kit</span>' +
          '<span class="ez-cfgsk__recap-sku">' + kitSku() + '</span></div>' +
          '<div class="ez-cfgsk__recap-body">' + st.band.material + ' ft of ' + cName + ' skirting material (' + hLabel + ') · ' +
          slideDisplay() + ' · ' + snapsPhrase() + ' · Gecko Velcro™ ' +
          (st.height === 'T' ? '5 ft' : '4 ft') + ' strip · primer pens · marking pencil · cutting &amp; removal tool · screws · pins · instructions</div>';
      }
      renderAddons();
    }

    function addonRow(opts) {
      // opts: {key,title,sub,price,on,rec,extra,extra_html}
      var wrap = document.createElement('div');
      wrap.className = 'ez-cfgsk__addon' + (opts.on ? ' is-on' : '');
      wrap.innerHTML =
        '<div class="ez-cfgsk__addon-main">' +
          '<span class="ez-cfgsk__check' + (opts.on ? ' is-on' : '') + '" aria-hidden="true">' +
            (opts.on ? '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2l2.2 2.2 4.8-4.8" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' : '') +
          '</span>' +
          '<div class="ez-cfgsk__addon-txt">' +
            '<div class="ez-cfgsk__addon-titlerow"><span class="ez-cfgsk__addon-title">' + opts.title + '</span>' +
              (opts.rec ? '<span class="ez-cfgsk__tag ez-cfgsk__tag--rec">Recommended</span>' : '') +
              (opts.extra ? '<span class="ez-cfgsk__tag">Extra</span>' : '') + '</div>' +
            '<div class="ez-cfgsk__addon-sub">' + opts.sub + '</div>' +
            '<div class="ez-cfgsk__addon-extra">' + (opts.on ? (opts.extra_html || '') : '') + '</div>' +
          '</div>' +
        '</div>' +
        '<span class="ez-cfgsk__addon-price">' + opts.price + '</span>';
      wrap.querySelector('.ez-cfgsk__addon-main').addEventListener('click', function (e) {
        if (e.target.closest('[data-ez-cfgsk-stop]')) return;
        opts.toggle();
      });
      return wrap;
    }

    // Nested "matched extras" under the hitch upsell: pooled clamps (delta price) and the 74″ hitch bag. Each is
    // a real toggle; the clamp row's price is the pooling delta (display). The hitch's snap pack is not a toggle:
    // it always ships, studs in the build's stud color (no second upgrade charge).
    function renderHitchExtras() {
      var wrap = document.createElement('div');
      wrap.className = 'ez-cfgsk__hnest';
      var label = document.createElement('div');
      label.className = 'ez-cfgsk__hnest-label';
      label.innerHTML = 'Matched to your skirting extras<span class="ez-cfgsk__hnest-hint">Pre-set to match your build — the hitch color matches your skirting automatically. Its ' + HITCH_SNAP_COUNT + ' snaps are included, studs in your ' + engine.colorName(studCode()) + ' stud color. Uncheck anything you don’t want.</span>';
      wrap.appendChild(label);

      // clamps — pooled delta (or its own 25-pack when the skirt isn't clamped)
      var delta = hitchClampDelta();
      var tag, sub;
      if (!st.clampsOn) { tag = 'its own ' + packFor(HITCH_CLAMP_RAW) + '-pack'; sub = 'Your skirting isn’t clamped, so the hitch’s clamps come as their own ' + packFor(HITCH_CLAMP_RAW) + '-pack — same as the standalone hitch page.'; }
      else if (delta === 0) { tag = 'pooled · free'; sub = 'The hitch’s clamps fit in your ' + effClampPack() + '-count skirting pack — no second pack.'; }
      else { tag = 'pooled · bumps pack'; sub = 'Adding the hitch tips your skirting pack up one size to cover it — still one pack.'; }
      var clampFree = (st.clampsOn && delta === 0);
      var clampRow = addonRow({
        title: 'Pipe clamps<span class="ez-cfgsk__htag' + (clampFree ? ' ez-cfgsk__htag--free' : '') + '">' + tag + '</span>',
        price: (clampFree ? '+' + money(0) : '+' + money(delta)), on: st.hitchClampOn, sub: sub,
        toggle: function () { st.hitchClampOn = !st.hitchClampOn; renderAddons(); renderAll(); }
      });
      clampRow.classList.add('ez-cfgsk__hrow');
      if (clampFree) clampRow.classList.add('ez-cfgsk__hrow--free');
      wrap.appendChild(clampRow);

      // bag — 74″ hitch bag (default off)
      var bagRow = addonRow({
        title: 'Storage bag<span class="ez-cfgsk__htag ez-cfgsk__htag--muted">Hitch 74″</span>',
        price: '+' + money(hitchBagPrice || 0), on: st.hitchBagOn,
        sub: 'Folds the hitch enclosure panel away for the off-season. Separate from your skirting bag.',
        toggle: function () { st.hitchBagOn = !st.hitchBagOn; renderAddons(); renderAll(); }
      });
      bagRow.classList.add('ez-cfgsk__hrow');
      wrap.appendChild(bagRow);
      return wrap;
    }

    function renderAddons() {
      var host = $('[data-ez-cfgsk-addons]');
      if (!host) return;
      host.innerHTML = '';

      // 5th-wheel hitch enclosure — the top upsell for fifth-wheels (the raised nose the side skirting
      // can't reach). Only shown when the rig is a fifth-wheel; opt-in (default OFF), color-matched to the skirting.
      if (st.rvType === 'fifth_wheel' && hitchAvailable()) {
        var hitchNode = addonRow({
          title: 'Add the 5th-wheel hitch enclosure', price: money(hitchNcPrice), on: st.hitchOn, rec: true,
          sub: 'Your side skirting stops behind the gooseneck. This pre-sewn 71″ panel closes the raised front hitch area, color-matched to your ' + engine.colorName(st.color) + ' skirting. No separate connector needed: your side skirting seals the back. Sold separately.',
          // On first add, default the matched extras: clamps on (pooled), bag stays off. (Its snap pack always ships.)
          toggle: function () { st.hitchOn = !st.hitchOn; if (st.hitchOn) { st.hitchClampOn = true; } renderAddons(); renderAll(); }
        });
        host.appendChild(hitchNode);
        if (st.hitchOn) host.appendChild(renderHitchExtras());
      }

      var cName = engine.colorName(st.color);
      var clampPack = effClampPack();
      var clampLine = engine.buildKitLines({ band: st.band, height: st.height, color: st.color, clampsOn: true, clampPack: clampPack })
        .filter(function (l) { return l.sku.indexOf('EZ-B-CMP') === 0; })[0];
      var clampPrice = clampLine ? clampLine.price : 0;

      // clamps (recommended, pre-selected). When the hitch enclosure's clamps pool in, this pack bumps to cover
      // skirting + hitch as ONE pack, and the ★ recommendation follows the pooled size.
      var clampExtra = '';
      if (st.clampsOn) {
        clampExtra = '<div class="ez-cfgsk__packrow"><span class="ez-cfgsk__packlabel">Pack:</span>' +
          [25, 50, 75, 100].map(function (n) {
            return '<button type="button" data-ez-cfgsk-stop data-clamppack="' + n + '" class="ez-cfgsk__pack' +
              (clampPack === n ? ' is-active' : '') + '">' + n + (n === pooledClampPack() ? ' ★' : '') + '</button>';
          }).join('') + '</div>';
      }
      var clampNode = addonRow({
        title: 'Pipe clamps', price: money(clampPrice), on: st.clampsOn, rec: true, extra_html: clampExtra,
        sub: 'Anchor the skirt to a PVC pipe frame you supply (24″ spacing) — best for high wind or soft ground. Frame not included. ' +
          (hitchClampsPooling() ? 'Sized to cover your skirting + hitch: ' + pooledClampPack() + '-count.' : 'Recommended: ' + pooledClampPack() + '-count pack.'),
        toggle: function () { st.clampsOn = !st.clampsOn; renderAddons(); renderAll(); }
      });
      host.appendChild(clampNode);
      clampNode.querySelectorAll('[data-clamppack]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.stopPropagation(); st.clampPack = Number(btn.dataset.clamppack); renderAddons(); renderAll(); });
      });

      // (Stud color is NOT an add-on: it is a main choice in step 3, right under the skirting color — renderStudPick.)

      // extra velcro
      var velLines = engine.buildKitLines({ band: st.band, height: st.height, color: st.color, velcroOn: true, velFt: st.velFt })
        .filter(function (l) { return l.sku.indexOf('EZ-C-VEL') === 0; });
      var velPrice = velLines[0] ? velLines[0].price : 0;
      var velExtra = '';
      if (st.velcroOn) {
        velExtra = '<div class="ez-cfgsk__packrow">' + [10, 20].map(function (ft) {
          return '<button type="button" data-ez-cfgsk-stop data-velft="' + ft + '" class="ez-cfgsk__pack' + (st.velFt === ft ? ' is-active' : '') + '">' + ft + ' ft</button>';
        }).join('') + '</div>';
      }
      var velNode = addonRow({
        title: 'Extra Gecko Velcro™', price: money(velPrice), on: st.velcroOn, extra: true, extra_html: velExtra,
        sub: 'Your kit already includes one ' + (st.height === 'T' ? '5 ft' : '4 ft') + ' strip. Add more for extra doors, bays, or hatches.',
        toggle: function () { st.velcroOn = !st.velcroOn; renderAddons(); renderAll(); }
      });
      host.appendChild(velNode);
      velNode.querySelectorAll('[data-velft]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.stopPropagation(); st.velFt = Number(btn.dataset.velft); renderAddons(); renderAll(); });
      });

      // storage bag
      var bagLine = engine.buildKitLines({ band: st.band, height: st.height, color: st.color, bagOn: true })
        .filter(function (l) { return l.sku.indexOf('EZ-C-SKB') === 0; })[0];
      var bagPrice = bagLine ? bagLine.price : 0;
      host.appendChild(addonRow({
        title: 'Storage bag', price: money(bagPrice), on: st.bagOn, extra: true,
        sub: 'Heavy-duty bag to protect your skirting in the off-season.',
        toggle: function () { st.bagOn = !st.bagOn; renderAddons(); renderAll(); }
      }));
    }

    function renderReview() {
      if (!coreResolved()) return;
      var host = $('[data-ez-cfgsk-review]');
      var totalEl = $('[data-ez-cfgsk-total]');
      if (!host) return;
      host.innerHTML = '';
      var snapsAdded = false;
      var ln = lines();
      ln.forEach(function (l) {
        var row = document.createElement('div');
        row.className = 'ez-cfgsk__rrow';
        // The skirting's snap packs read as ONE row (pack chips), like the old stud-pack row. Included ($0).
        if (l.kitSnaps && !l.hitch) {
          if (snapsAdded) return;
          snapsAdded = true;
          var packs = [];
          ln.filter(function (x) { return x.kitSnaps && !x.hitch; })
            .forEach(function (x) { for (var i = 0; i < x.qty; i++) packs.push(x.packN); });
          row.innerHTML = '<div><div class="ez-cfgsk__rname">Snap packs</div>' +
            '<div class="ez-cfgsk__rpacks">' + packs.map(function (p) { return '<span class="ez-cfgsk__rpack">' + p + '</span>'; }).join('') +
            '<span class="ez-cfgsk__rmeta">· ' + engine.colorName(st.color) + ' caps · ' + engine.colorName(studCode()) + ' studs · ' + packs.length + ' pack' + (packs.length > 1 ? 's' : '') + '</span></div></div>' +
            '<div class="ez-cfgsk__rprice">Included</div>';
          host.appendChild(row);
          return;
        }
        var isKit = l.sku.indexOf('EZ-B-SKT') === 0;
        var plain = !l.kitSnaps && !l.studFee;
        row.className += isKit ? ' ez-cfgsk__rrow--kit' : '';
        row.innerHTML = '<div><div class="ez-cfgsk__rname">' + l.title + '</div>' +
          (l.opts ? '<div class="ez-cfgsk__ropts">' + l.opts + '</div>' : '') +
          (l.sku ? '<div class="ez-cfgsk__rsku">' + l.sku + '</div>' : '') + '</div>' +
          '<div class="ez-cfgsk__rprice">' + lineAmtText(l) + (plain && l.qty > 1 ? '<div class="ez-cfgsk__rqty">Qty ' + l.qty + ' · ' + money(l.price) + ' ea</div>' : '') + '</div>';
        host.appendChild(row);
      });
      if (totalEl) totalEl.textContent = money(total());
    }

    function renderAll() {
      renderRail(); renderRig(); renderStepChips(); renderResolve(); renderHeightPrices();
      renderSwatches(); renderStudPick();
      if (coreResolved()) { renderStep4(); renderReview(); }
      // lock/unlock gating
      setLocked(2, !s1done());
      setLocked(3, !st.height);
      setLocked(4, !coreResolved());
      setLocked(5, !coreResolved());
      // enable each step's Continue button only once its pick is made
      var c2 = $('[data-ez-cfgsk-cont2]'); if (c2) c2.disabled = !st.height;
      var c3 = $('[data-ez-cfgsk-cont3]'); if (c3) c3.disabled = !st.color;
      // re-apply peek to the step after open
      setOpen(st.open);
      persist();
    }

    // ── interactions ──
    var resolveTimer;
    function liveResolve(val) {
      var v = parseFloat(val);
      st.len = isNaN(v) ? null : v;
      resolveBand();          // length + type + slides → the (possibly slide-bumped) band
      st.clampPack = null;
      // changing size invalidates a prior review-lock band
      renderAll();
      scheduleCapture();      // log this resolved RV (debounced + deduped)
    }
    lenInput.addEventListener('input', function () {
      setMethod('manual'); // typing a length commits to manual — collapses the RV lookup
      // A YMM lookup is superseded by typing a length; but a CUSTOM "not in our list" entry is a component of
      // the manual method (the customer typed the length AND told us the rig), so keep it while they edit length.
      if (st.lookupMeta && !st.lookupMeta.custom) { st.lookupMeta = null; ymmClearResolved(); }
      clearTimeout(resolveTimer);
      resolveTimer = setTimeout(function () { liveResolve(lenInput.value); }, 400);
    });
    lenInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); clearTimeout(resolveTimer); liveResolve(lenInput.value); if (s1done()) setOpen(2, true); }
    });

    // no auto-advance — an explicit Continue button at the step bottom advances (no surprise jump)
    function pickHeight(h) { st.height = h; renderAll(); }
    // Studs follow the skirting color (included) unless the shopper picked a different stud color; a pick that
    // now equals the new skirting color collapses back to the included match.
    function pickColor(c) { st.color = c; if (st.studColor === c) st.studColor = null; renderAll(); }

    // ── Method A: "Look up my RV" — Year → Make → Model → Floorplan cascade that resolves the RV length.
    //    Fitment data (tree = { year: { make: { model: { floorplan: ft10 } } } }, ft10 = tenths of feet) is a
    //    static asset lazy-fetched on first interaction/idle so it never touches initial paint / LCP.
    var ymmBox = $('[data-ez-cfgsk-ymm]');
    function ymmClearResolved() { if (ymmBox) ymmBox.classList.remove('is-resolved'); }
    function ftLabel(t) {
      var ft = Math.floor(t / 10);
      var inch = Math.round((t / 10 - ft) * 12);
      if (inch === 12) return (ft + 1) + "'";
      return inch ? (ft + "' " + inch + '"') : (ft + "'");
    }
    function initYmm() {
      if (!ymmBox) return;
      var src = ymmBox.getAttribute('data-ez-cfgsk-fitment');
      var selYear = $('[data-ez-cfgsk-ymm-field="year"]'), selMake = $('[data-ez-cfgsk-ymm-field="make"]');
      var selModel = $('[data-ez-cfgsk-ymm-field="model"]'), selFp = $('[data-ez-cfgsk-ymm-field="fp"]');
      var hint = $('[data-ez-cfgsk-ymm-hint]'), foot = $('[data-ez-cfgsk-ymm-foot]');
      if (!(src && selYear && selMake && selModel && selFp)) return;
      var data = null, fetched = false, sel = { year: '', make: '', model: '', fp: '' };

      function opt(v, label) { var o = document.createElement('option'); o.value = v; o.textContent = label; return o; }
      function fill(select, placeholder, items, labeler) {
        select.innerHTML = ''; select.appendChild(opt('', placeholder));
        items.forEach(function (it) { select.appendChild(labeler(it)); });
      }
      function setVal(select, v) { select.value = v; select.classList.toggle('has-value', !!v); }
      function reset(select, placeholder) { select.disabled = true; select.innerHTML = ''; select.appendChild(opt('', placeholder)); setVal(select, ''); }
      function years() { return data ? Object.keys(data).sort(function (a, b) { return Number(b) - Number(a); }) : []; }
      function makes() { return (data && sel.year) ? Object.keys(data[sel.year] || {}).sort() : []; }
      function models() { return (data && sel.year && sel.make) ? Object.keys((data[sel.year] || {})[sel.make] || {}).sort() : []; }
      function fps() {
        var o = data && data[sel.year] && data[sel.year][sel.make] && data[sel.year][sel.make][sel.model];
        if (!o) return [];
        return Object.keys(o).map(function (k) { return [k, o[k]]; }).sort(function (a, b) { return a[1] - b[1]; });
      }
      function resolve(ft10, meta) {
        lenInput.value = String(ft10 / 10);
        st.lookupMeta = meta;
        // A newly looked-up RV is a DIFFERENT rig — drop any type/slide count the customer set for the
        // previous one (YMM fitment carries no type/slide data, so these are unknown until re-stated), and
        // clear any half-filled "not in our lookup" fields (a real match supersedes the custom entry).
        st.rvType = 'trailer'; st.rvTypeSet = false; st.slides = null;
        $$('[data-ez-cfgsk-other-field]').forEach(function (el) { el.value = ''; });
        liveResolve(String(ft10 / 10));   // sets band + re-renders the resolve box (Continue-to-height)
        ymmBox.classList.add('is-resolved');
      }

      selYear.addEventListener('change', function () {
        sel.year = selYear.value; sel.make = sel.model = sel.fp = ''; setVal(selYear, sel.year);
        if (sel.year) { fill(selMake, 'Make', makes(), function (m) { return opt(m, m); }); selMake.disabled = false; setVal(selMake, ''); } else reset(selMake, '—');
        reset(selModel, '—'); reset(selFp, '—'); if (hint) hint.textContent = '';
      });
      selMake.addEventListener('change', function () {
        sel.make = selMake.value; sel.model = sel.fp = ''; setVal(selMake, sel.make);
        if (sel.make) { fill(selModel, 'Model', models(), function (m) { return opt(m, m); }); selModel.disabled = false; setVal(selModel, ''); } else reset(selModel, '—');
        reset(selFp, '—'); if (hint) hint.textContent = '';
      });
      selModel.addEventListener('change', function () {
        sel.model = selModel.value; sel.fp = ''; setVal(selModel, sel.model);
        if (!sel.model) { reset(selFp, '—'); if (hint) hint.textContent = ''; return; }
        var list = fps();
        fill(selFp, list.length === 1 ? list[0][0] : 'Floorplan', list, function (e) { return opt(e[0], e[0] + ' — ' + ftLabel(e[1])); });
        selFp.disabled = false; setVal(selFp, '');
        if (hint) hint.textContent = list.length > 1 ? (list.length + ' floorplans') : '';
        if (list.length === 1) { sel.fp = list[0][0]; setVal(selFp, sel.fp); resolve(list[0][1], { year: sel.year, make: sel.make, model: sel.model, fp: sel.fp, ft10: list[0][1] }); }
      });
      selFp.addEventListener('change', function () {
        sel.fp = selFp.value; setVal(selFp, sel.fp);
        var o = data && data[sel.year] && data[sel.year][sel.make] && data[sel.year][sel.make][sel.model];
        var t = o && o[sel.fp];
        if (t != null) resolve(t, { year: sel.year, make: sel.make, model: sel.model, fp: sel.fp, ft10: t });
      });

      function load() {
        if (fetched) return; fetched = true;
        selYear.disabled = true; var y0 = selYear.querySelector('option'); if (y0) y0.textContent = 'Loading…';
        fetch(src).then(function (r) { if (!r.ok) throw new Error('http'); return r.json(); })
          .then(function (j) {
            data = j;
            fill(selYear, 'Year', years(), function (y) { return opt(y, y); }); selYear.disabled = false;
            if (foot) foot.textContent = '1,100+ models · 1998–2027 (RVUSA dimensions). Not listed? Enter your length below.';
          })
          .catch(function () { if (foot) foot.textContent = "Couldn't load the RV list — enter your length below instead."; });
      }
      ymmBox.addEventListener('pointerenter', load);
      ymmBox.addEventListener('focusin', load);
      ymmBox.addEventListener('touchstart', load, { passive: true });
      ymmBox.addEventListener('change', function () { setMethod('ymm'); }); // picking any lookup field commits to the RV lookup — collapses manual
      if (window.requestIdleCallback) window.requestIdleCallback(load); else setTimeout(load, 3000);
    }

    $$('[data-ez-cfgsk-height]').forEach(function (b) {
      b.addEventListener('click', function () { if (!s1done()) return; pickHeight(b.dataset.ezCfgskHeight); });
    });

    // ── RV type + slide-outs (refine the material need) ──
    $$('[data-ez-cfgsk-type]').forEach(function (b) {
      b.addEventListener('click', function () {
        var t = b.getAttribute('data-ez-cfgsk-type');
        if (!st.len) return;
        st.rvType = t; st.rvTypeSet = true;
        // Hitch enclosure is OPT-IN: never auto-added. Fifth-wheels see the recommended (unchecked) row and
        // opt in; switching to a travel trailer clears any prior choice (the add-on only applies to fifth-wheels).
        if (t !== 'fifth_wheel') st.hitchOn = false;
        resolveBand(); renderAll(); scheduleCapture();
      });
    });
    $$('[data-ez-cfgsk-slide]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (!st.len) return;
        var next = Math.max(0, Math.min(8, currentSlides() + Number(b.getAttribute('data-ez-cfgsk-slide'))));
        if (next === currentSlides()) return;
        st.slides = next;
        resolveBand(); renderAll(); scheduleCapture();
      });
    });

    // explicit step-advance buttons (Height -> Color, Color -> kit); disabled until the step's pick is made
    var cont2 = $('[data-ez-cfgsk-cont2]');
    if (cont2) cont2.addEventListener('click', function () { if (st.height) setOpen(3, true); });
    var cont3 = $('[data-ez-cfgsk-cont3]');
    if (cont3) cont3.addEventListener('click', function () { if (st.color) setOpen(4, true); });

    // step heads (accordion open on click if not locked)
    $$('[data-ez-cfgsk-stephead]').forEach(function (h) {
      h.addEventListener('click', function () {
        var n = Number(h.dataset.ezCfgskStephead);
        if (stepEl(n).classList.contains('is-locked')) return;
        setOpen(n);
      });
    });

    var toReview = $('[data-ez-cfgsk-toreview]');
    if (toReview) toReview.addEventListener('click', function () { st.reviewedBand = st.band.band; renderAll(); setOpen(5, true); });

    initYmm(); // Method A — Year/Make/Model/Floorplan lookup (lazy-loads its fitment data on first use)

    // ── Optional "RV not in our lookup" capture — always-visible fields directly under the length input,
    //    NEVER required. The length still sizes the kit; filling any of year/make/model tags the build as a
    //    CUSTOM entry (source:'other', found_in_db:false) so the rigs we don't list yet feed the backfill queue. ──
    function readOther() {
      var g = function (k) { var el = $('[data-ez-cfgsk-other-field="' + k + '"]'); return el ? el.value.trim() : ''; };
      return { year: g('year'), make: g('make'), model: g('model'), fp: g('fp') };
    }
    var otherTimer;
    function syncOther() {
      var o = readOther();
      if (o.year || o.make || o.model || o.fp) {
        // On the manual path a floorplan is a CAPTURED descriptor only (the typed length already sizes the
        // kit; resolving length from a floorplan needs the YMM fitment tree). It flows to _rv / rv_floorplan
        // / the recap RV name / capture dedup automatically via lookupMeta.fp — no other wiring needed.
        st.lookupMeta = { custom: true, year: o.year || null, make: o.make || null, model: o.model || null, fp: o.fp || null, ft10: null };
      } else if (st.lookupMeta && st.lookupMeta.custom) {
        st.lookupMeta = null;   // all fields cleared → revert to plain manual
      }
      renderAll();
      scheduleCapture();
    }
    $$('[data-ez-cfgsk-other-field]').forEach(function (el) {
      el.addEventListener('input', function () { clearTimeout(otherTimer); otherTimer = setTimeout(syncOther, 500); });
    });

    // ── ATC — ONE multi-line POST to /cart/add.js ──
    // Re-entrancy guard: the sticky bar fires form.requestSubmit(), which BYPASSES the in-card button's
    // disabled state — so a double-tap (esp. on mobile) would POST the whole build twice with distinct
    // _build ids. Both triggers route through this one submit handler, so a single flag guards both.
    var submitting = false;
    // Inline ATC failure notice under the in-card button (same treatment as the hitch builder's atc-error).
    // '' clears it. Shown when the snap packs can't resolve OR the POST fails: in both cases nothing was added.
    function showAtcError(msg) {
      var atcBtn = $('[data-ez-cfgsk-atc]');
      var el = root.querySelector('[data-ez-cfgsk-atcerr]');
      if (!msg) { if (el && el.parentNode) el.parentNode.removeChild(el); return; }
      if (!el && atcBtn && atcBtn.parentNode) {
        el = document.createElement('div');
        el.setAttribute('data-ez-cfgsk-atcerr', '');
        el.setAttribute('role', 'alert');
        el.style.cssText = 'margin-top:10px;padding:10px 12px;border-radius:8px;background:rgba(196,98,42,.09);color:#8a2b12;font-size:13px;line-height:1.45;';
        atcBtn.parentNode.insertBefore(el, atcBtn.nextSibling);
      }
      if (el) el.textContent = msg;
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitting) return;
      if (!coreResolved()) return;
      var bid = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      var bprops = { _build: bid, _build_kind: 'skirt', _build_label: 'RV skirting' };
      // Hidden RV descriptors (_-prefix = hidden from the customer, visible in the order admin) on the KIT
      // line only — the warehouse sees the rig + resolved material once, not repeated on every add-on line.
      var lm = st.lookupMeta;
      var rvProps = {
        _rv_length: (st.len != null ? String(st.len) + ' ft' : ''),
        _rv_type: rvTypeLabel() + (st.rvTypeSet ? '' : ' (assumed)'),
        _rv_slide_outs: (st.slides != null ? String(st.slides) : String(currentSlides()) + ' (assumed)'),
        _rv_material_ft: (st.band && st.band.material ? String(st.band.material) + ' ft' : ''),
        _rv_source: (lm ? (lm.custom ? 'Not in lookup (customer-entered)' : 'RV lookup') : 'Manual length')
      };
      if (lm) {
        var rvName = rvLabel(lm);
        if (rvName) rvProps._rv = rvName;
      }
      // Per-build cart image on the KIT line only. The skirting builder previously stamped no _build_img, so
      // the cart line fell back to the product featured image — a fifth-wheel with the FRONT HITCH ENCLOSED,
      // which implies the (separately-sold) hitch kit is included (Troy's "RV skirting image in cart is
      // misleading"). Stamp the approved side-skirting hero (Jim Editorial, smooth-sided Solitude, side
      // skirting only, no hitch) so the cart matches what actually ships. Add-on lines keep their own images.
      var kitImg = 'https://cdn.shopify.com/s/files/1/0630/7754/8129/files/jim-3688v3.jpg?v=1783989858';
      // VISIBLE (non-underscore) kit contents on the KIT line — Shopify renders non-_ line-item properties in
      // the cart/checkout summary, order-confirmation email, and admin, so this surfaces what's in the box AT
      // CHECKOUT (Troy: customers call asking what's included while they're checking out). Sourced BOM — the
      // same string the Klaviyo recap builds; height + color already show as the variant's own options.
      var kitIncludes = (st.band && st.band.material ? st.band.material + ' ft Diamond Weave™ skirting · ' : '') +
        snapsPhrase() + ' · Gecko Velcro™ ' +
        (st.height === 'T' ? '5 ft' : '4 ft') + ' strip · primer pens · marking pencil · cutting & removal tool · screws · pins · instructions';
      var visProps = { 'Kit includes': kitIncludes };
      // items[] keeps lines() order. Snap packs are display lines (no variantId): each kit's packs (skirting,
      // and the nested hitch's) become ONE window.EZKitSnaps.lines() request, held as a null slot at the
      // position of that kit's first pack and filled once the snap-pack variants resolve. The stud upgrade
      // line is display only (the server charges it on the snap lines) and is never posted.
      var ln = lines();
      var kitQty = 1, hitchQty = 1, hasKit = false;
      ln.forEach(function (l) {
        if (l.sku.indexOf('EZ-B-SKT') === 0) kitQty = l.qty || 1;
        else if (l.sku.indexOf('EZ-B-HIT') === 0) hitchQty = l.qty || 1;
      });
      var items = [], snapReqs = [], reqByKit = {};
      ln.forEach(function (l) {
        if (l.studFee) return;
        if (l.kitSnaps) {
          var key = l.hitch ? 'hitch' : 'skirt';
          var r = reqByKit[key];
          if (!r) {
            r = reqByKit[key] = { slot: items.length, packs: [], cap: l.cap, stud: l.stud, kitQty: l.hitch ? hitchQty : kitQty };
            snapReqs.push(r);
            items.push(null);
          }
          r.packs.push({ n: l.packN, qty: l.qty });
          return;
        }
        var id = l.variantId || (addonBySku[l.sku] || {}).id;
        if (!id) return;
        if (l.sku.indexOf('EZ-B-SKT') === 0) hasKit = true;
        // Kit line carries the RV descriptors + image; hitch matched-extras carry a visible "For" label so they
        // read as the hitch's own line (and don't silently merge a shared SKU the skirting also uses into a
        // confusing qty 2); everything else gets the bare build props.
        var props = (l.sku.indexOf('EZ-B-SKT') === 0) ? Object.assign({}, bprops, rvProps, visProps, { _build_img: kitImg })
          : (l.hitch ? Object.assign({}, bprops, { 'For': '5th-wheel hitch enclosure' }) : bprops);
        items.push({ id: Number(id), quantity: l.qty, properties: props });
      });
      var atcFail = 'We couldn’t add your build, so nothing was added to your cart. We won’t ship a partial order. Please refresh and try again, or contact us and we’ll place it for you.';
      if (!hasKit) { showAtcError(atcFail); return; }   // never post add-ons/snaps without the kit itself
      submitting = true;
      var atc = $('[data-ez-cfgsk-atc]');
      if (atc) { atc.disabled = true; atc.textContent = 'Adding…'; }
      showAtcError('');
      // All-or-nothing: resolve EVERY kit's snap-pack lines first. Snap lines carry only the build's grouping
      // props (bprops: _build/_build_kind/_build_label; the helper adds _kit_snaps), never the visible
      // "Kit includes" or the kit's _build_img. Any failure (helper missing, a pack variant missing, no packs
      // for the kit) rejects before the POST, so a kit is never added without its snaps.
      var K = window.EZKitSnaps;
      var snapsReady = Promise.resolve().then(function () {   // a sync throw also lands in the catch below
        if (!K || typeof K.lines !== 'function' || !reqByKit.skirt) throw new Error('kit snaps unavailable');
        return Promise.all(snapReqs.map(function (r) {
          return K.lines({ packs: r.packs, cap: r.cap, stud: r.stud, kitQty: r.kitQty, props: bprops })
            .then(function (got) { if (!got || !got.length) throw new Error('kit snaps: no pack lines'); return got; });
        }));
      });
      snapsReady.then(function (res) {
        var bySlot = {};
        snapReqs.forEach(function (r, i) { bySlot[r.slot] = res[i]; });
        var payload = [];
        items.forEach(function (it, i) {
          if (it) payload.push(it);
          else bySlot[i].forEach(function (s) { payload.push({ id: Number(s.id), quantity: s.quantity, properties: s.properties }); });
        });
        return fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: payload })
        });
      }).then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
        .then(function (data) {
          // Drawer by default; hard /cart redirect only when the section opts out.
          if (root.getAttribute('data-cart-redirect') === 'true') { window.location.href = '/cart'; return; }
          submitting = false;
          if (atc) { atc.disabled = false; atc.textContent = (root.dataset.atcLabel || 'Add my kit to cart →'); }
          document.dispatchEvent(new CustomEvent('ez:cart:added', { detail: { cart: data } }));
        })
        .catch(function () {
          submitting = false;
          if (atc) { atc.disabled = false; atc.textContent = (root.dataset.atcLabel || 'Add my kit to cart →'); }
          showAtcError(atcFail);
        });
    });

    // ── share / restore build via ?len&h&c&cl&st&ve&bg query params ──
    function buildParams() {
      var p = new URLSearchParams();
      if (st.len != null) p.set('len', String(st.len));
      if (st.rvTypeSet) p.set('ty', { trailer: 'tt', fifth_wheel: 'fw', class_a: 'ca', class_b: 'cb', class_c: 'cc', park_model: 'pm' }[st.rvType] || 'tt');
      if (st.rvType === 'fifth_wheel') p.set('hk', st.hitchOn ? '1' : '0'); // hitch upsell state (fifth-wheel only) — keeps a resumed/emailed build's hitch choice
      if (st.rvType === 'fifth_wheel' && st.hitchOn) {   // hitch matched-extras state (only when the hitch is added)
        p.set('hcl', st.hitchClampOn ? '1' : '0');
        p.set('hbg', st.hitchBagOn ? '1' : '0');
      }
      if (st.slides != null) p.set('sl', String(st.slides));
      if (st.height) p.set('h', st.height);
      if (st.color) p.set('c', st.color);
      // Clamp param: '0' off · a NUMBER = explicit pack override · '1' = on with no override (let the engine pick
      // the — possibly hitch-pooled — default). Encoding the default as '1' (not its number) stops a RESTORED
      // build from pinning the skirt-only pack, which would defeat the hitch clamp pooling. null-safe (runs pre-band).
      if (!st.clampsOn) p.set('cl', '0');
      else if (st.clampPack) p.set('cl', String(st.clampPack));
      else p.set('cl', '1');
      if (studDiff()) p.set('st', st.studColor);   // stud color only when it differs from the skirting (match = no param)
      if (st.velcroOn) p.set('ve', String(st.velFt));
      if (st.bagOn) p.set('bg', '1');
      return p.toString();
    }
    function buildUrl() { return window.location.origin + window.location.pathname + '?' + buildParams(); }
    // Persist the build so it survives a refresh AND a browser close (localStorage),
    // and mirror it to the URL (shareable + refresh-proof) — matching hitch/shade.
    // Guarded on st.band: nothing meaningful to save before a size resolves. That guard
    // also makes the init renderAll() (which runs BEFORE restore()) a no-op here, so a
    // saved build is never clobbered by the default state at load.
    function persist() {
      if (!st.band || st.band.oversize) return;   // never persist an oversize state (restore rejects it)
      var params = buildParams();
      try { localStorage.setItem('ezsnap_skirting', params); } catch (e) {}
      try { window.history.replaceState(null, '', window.location.pathname + '?' + params); } catch (e) {}
    }
    var copyBtn = $('[data-ez-cfgsk-copy]');
    if (copyBtn) copyBtn.addEventListener('click', function () {
      var url = buildUrl();
      var done = function () { copyBtn.textContent = 'Link copied ✓'; setTimeout(function () { copyBtn.textContent = (root.dataset.shareCta || 'Copy my build link'); }, 2200); };
      if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, done); else done();
    });

    // ── "Email me this build" → Klaviyo "Submitted Configuration" (RMMHhi), the SAME event/flow the shade
    //    builder fires: the shared "Configurator Build Recap" flow (XVmUBv) loops a generic recap template, so
    //    skirting renders from the payload below (product_noun / specs / line_items / value_points). The flow
    //    currently sends as a MARKETING message (transactional under review → auto-upgrades on approval);
    //    marketing is consent-gated, so non-subscribers are skipped. Klaviyo's flow live/draft is the real on/off. ──
    // Human RV label from a lookupMeta: "<year> <make> <model> · <floorplan>", robust to a lone floorplan
    // (manual-path backfill can carry just a floorplan) — no leading separator when year/make/model are empty.
    function rvLabel(lm) {
      if (!lm) return '';
      var base = [lm.year, lm.make, lm.model].filter(Boolean).join(' ');
      return lm.fp ? (base ? base + ' · ' + lm.fp : lm.fp) : base;
    }

    function emailBuild(email) {
      if (!coreResolved()) return false;
      var ln = lines();
      var hLabel = st.height === 'T' ? '60″ Tall' : '46″ Standard';
      // The YMM lookup (year/make/model/floorplan) is captured in st.lookupMeta when the customer used
      // "Look up my RV" — surface it as an "RV" row so the recap names the actual rig, not just the sized-to
      // length. Manual-length builds have no lookupMeta, so the row is omitted. (Same string as the on-page
      // resolve box: "<year> <make> <model> · <floorplan>".)
      var lm = st.lookupMeta;
      var rvName = rvLabel(lm);
      var specs = [];
      if (rvName) specs.push({ label: 'RV', value: rvName });
      specs.push({ label: 'RV length', value: (st.len != null ? st.len + ' ft' : st.band.label) });
      specs.push({ label: 'RV type', value: rvTypeLabel() + (st.rvTypeSet ? '' : ' (assumed)') });
      specs.push({ label: 'Slide-outs', value: (st.slides != null ? String(st.slides) : currentSlides() + ' (assumed)') });
      specs.push({ label: 'Kit', value: st.band.label });
      specs.push({ label: 'Height', value: hLabel });
      specs.push({ label: 'Color', value: engine.colorName(st.color) });
      // Stud color is a main choice (not an add-on): its own row, right after the skirting color.
      specs.push({ label: 'Stud color', value: studDiff()
        ? engine.colorName(st.studColor) + ' (+' + upgradeText() + ' stud color upgrade)'
        : engine.colorName(st.color) + ' (matches your skirting · included)' });
      // Kit bill-of-materials — same contents string the on-page recap shows (sourced, not fabricated).
      // Raw "&" (not &amp;) because the Klaviyo recap template renders {{ spec.value }} unescaped.
      specs.push({ label: 'Kit includes', value:
        snapsPhrase() + ' · Gecko Velcro™ ' +
        (st.height === 'T' ? '5 ft' : '4 ft') + ' strip · primer pens · marking pencil · cutting & removal tool · screws · pins · instructions' });
      var addons = [];
      if (st.clampsOn) addons.push('Pipe clamps' + (st.clampPack ? ' (' + st.clampPack + '-pack)' : ''));
      if (st.velcroOn) addons.push('Extra Gecko Velcro™' + (st.velFt ? ' (' + st.velFt + ' ft)' : ''));
      if (st.bagOn) addons.push('Storage bag');
      if (st.hitchOn) {
        addons.push('5th-wheel hitch enclosure');
        if (st.hitchBagOn) addons.push('Hitch storage bag');
      }
      if (addons.length) specs.push({ label: 'Add-ons', value: addons.join(' · ') });
      var summary = ln.map(function (l) { return l.qty + '× ' + l.title + ' — ' + lineAmtText(l); }).join('\n');
      var grand = total();
      var props = {
        customer_email: email,
        config_type: 'skirting',
        product_noun: 'skirting',            // drives the recap headline: "Your {noun} build, saved."
        specs: specs,
        // Sourced + compliant skirting claims only (no retired sub-zero rating / install-time claim).
        value_points: ['Diamond Weave™ built for extreme cold', 'No-drill patented snap', 'Limited Lifetime warranty'],
        total: grand,
        total_display: money(grand),         // currency-aware: "CA$2,285" for a Canadian shopper
        currency: (window.EZ && window.EZ.ccy) || 'USD', // ISO code of total/price/amount (presentment currency)
        build_url: window.location.href,     // carries ?len&h&c&cl&st&ve&bg (persist mirrors it) -> recap "resume" link
        line_items: ln.map(function (l) { return { title: l.title, sku: l.sku, qty: l.qty, price: l.price, amount: lineAmtText(l), opts: l.opts || '' }; }),
        summary: summary,
        // flat convenience for the team notification / analytics + Klaviyo segmentation:
        rv_length: st.len,
        rv_type: st.rvTypeSet ? st.rvType : null,
        rv_slide_outs: (st.slides != null ? st.slides : null),
        rv: rvName || null,
        rv_year: lm ? lm.year : null,
        rv_make: lm ? lm.make : null,
        rv_model: lm ? lm.model : null,
        rv_floorplan: lm ? lm.fp : null,
        kit: st.band.label,
        height: hLabel,
        color: engine.colorName(st.color)
      };
      hubspotQuoteSubmit(email, props);
      return klaviyoTrack('Submitted Configuration', email, props);
    }

    var emailWrap = $('[data-ez-cfgsk-emailshare]');
    if (emailWrap) {
      var emInput = emailWrap.querySelector('[data-ez-cfgsk-emailinput]');
      var emBtn = emailWrap.querySelector('[data-ez-cfgsk-emailbtn]');
      var emMsg = emailWrap.querySelector('[data-ez-cfgsk-emailmsg]');
      var emLabel = emBtn ? emBtn.textContent : 'Email my build →';
      var emSend = function () {
        var email = ((emInput && emInput.value) || '').trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          if (emMsg) { emMsg.textContent = 'Please enter a valid email.'; emMsg.className = 'ez-cfgsk__emailshare-msg is-err'; }
          if (emInput) emInput.focus();
          return;
        }
        if (!coreResolved()) {
          if (emMsg) { emMsg.textContent = 'Finish your build first — pick a size, height and color.'; emMsg.className = 'ez-cfgsk__emailshare-msg is-err'; }
          return;
        }
        if (emBtn) emBtn.disabled = true;
        if (emInput) emInput.disabled = true;
        var ok = emailBuild(email);
        if (ok) {
          if (emMsg) { emMsg.textContent = 'Sent ✓ Check your inbox for your build.'; emMsg.className = 'ez-cfgsk__emailshare-msg is-ok'; }
          if (emBtn) emBtn.textContent = 'Sent ✓';
        } else {
          if (emMsg) { emMsg.textContent = 'Sorry, that didn’t send — please try again.'; emMsg.className = 'ez-cfgsk__emailshare-msg is-err'; }
          if (emBtn) { emBtn.disabled = false; emBtn.textContent = emLabel; }
          if (emInput) emInput.disabled = false;
        }
      };
      if (emBtn) emBtn.addEventListener('click', emSend);
      if (emInput) emInput.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); emSend(); } });
    }

    function restore() {
      var sp = new URLSearchParams(window.location.search);
      var len = sp.get('len');
      if (!len) {
        // No build in the URL (a normal refresh) → fall back to the saved build, so it
        // survives a refresh AND a browser close. A shared ?len=… link still wins above.
        var saved = null;
        try { saved = localStorage.getItem('ezsnap_skirting'); } catch (e) {}
        if (!saved) return;
        sp = new URLSearchParams(saved);
        len = sp.get('len');
        if (!len) return;
      }
      var v = parseFloat(len);
      st.len = isNaN(v) ? null : v;
      var ty = sp.get('ty');
      if (ty === 'fw') { st.rvType = 'fifth_wheel'; st.rvTypeSet = true; st.hitchOn = sp.get('hk') === '1';
        // hitch matched-extras: bag default off unless '1'; clamps default ON unless explicitly '0'. (A legacy
        // 'hs' hitch-studs param is ignored: the hitch's snap pack always ships, studs in the build's stud color.)
        if (st.hitchOn) { st.hitchClampOn = sp.get('hcl') !== '0'; st.hitchBagOn = sp.get('hbg') === '1'; }
      }
      else if (ty === 'tt') { st.rvType = 'trailer'; st.rvTypeSet = true; }
      else if (ty === 'ca') { st.rvType = 'class_a'; st.rvTypeSet = true; }
      else if (ty === 'cb') { st.rvType = 'class_b'; st.rvTypeSet = true; }
      else if (ty === 'cc') { st.rvType = 'class_c'; st.rvTypeSet = true; }
      else if (ty === 'pm') { st.rvType = 'park_model'; st.rvTypeSet = true; }
      else if (ty === 'mh') { st.rvType = 'class_a'; st.rvTypeSet = true; } // legacy motorhome share-link -> Class A
      else if (sp.get('h') && sp.get('c')) { st.rvTypeSet = true; } // legacy COMPLETE build shared before the type gate (no ty param) → honor its assumed-trailer sizing on resume, don't re-prompt. A mid-build refresh with no height/color keeps type unset so the "pick your RV type" prompt still shows.
      var slp = sp.get('sl'); if (slp != null && slp !== '') { var sn = parseInt(slp, 10); if (!isNaN(sn)) st.slides = Math.max(0, Math.min(8, sn)); }
      resolveBand();
      if (!st.band || st.band.oversize) return;
      lenInput.value = len;
      setMethod('manual'); // a restored build shows its length in manual view; the lookup collapses (switch back anytime)
      var h = sp.get('h'); if (h === 'S' || h === 'T') st.height = h;
      var c = sp.get('c'); if (COLORS.some(function (x) { return x.code === c; })) st.color = c;
      var cl = sp.get('cl');
      if (cl === '0') st.clampsOn = false;
      else if (cl === '1') { st.clampsOn = true; st.clampPack = null; }   // on, no override → engine/pooled default
      else if (cl) { st.clampsOn = true; st.clampPack = Number(cl); }     // explicit pack override
      // 'st' = a stud color different from the skirting (also how old links encoded the paid RV-color studs)
      var stc = sp.get('st'); if (stc && stc !== st.color && COLORS.some(function (x) { return x.code === stc; })) st.studColor = stc;
      var ve = sp.get('ve'); if (ve) { st.velcroOn = true; st.velFt = Number(ve); }
      if (sp.get('bg') === '1') st.bagOn = true;
      if (st.height && st.color) { st.reviewedBand = st.band.band; renderAll(); setOpen(5); }
      else { renderAll(); setOpen(st.height ? 3 : 2); }
    }

    // ── gallery ──
    var galMain = root.querySelector('[data-ez-cfgsk-galmain]');
    var figHost = root.querySelector('.ez-cfgsk__figcap');
    $$('[data-ez-cfgsk-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        if (galMain && t.dataset.src) galMain.src = t.dataset.src;
        $$('[data-ez-cfgsk-thumb]').forEach(function (x) { x.classList.remove('is-active'); });
        t.classList.add('is-active');
        var cap = figHost ? figHost.querySelector('.ez-fig__text') : null;
        if (cap) cap.textContent = t.dataset.cap || '';
      });
    });

    // ── Step-1 "or" switch links: collapse the current method, expand the other (fully reversible) ──
    $$('[data-ez-cfgsk-to]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var to = btn.getAttribute('data-ez-cfgsk-to');
        setMethod(to);
        if (to === 'manual') { if (lenInput) lenInput.focus(); }
        else if (to === 'ymm' && ymmBox) { var y = ymmBox.querySelector('[data-ez-cfgsk-ymm-field="year"]'); if (y) y.focus(); }
      });
    });

    renderAll();
    restore();

  }

  function bootAll() {
    if (!window.EZSnap || !window.EZSnap.createSkirtingEngine) return false;
    var roots = document.querySelectorAll('[data-ez-cfgsk]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__ezCfgskBooted) continue;
      roots[i].__ezCfgskBooted = true;
      initRoot(roots[i]);
    }
    return true;
  }

  if (!bootAll()) {
    document.addEventListener('DOMContentLoaded', bootAll);
    window.addEventListener('load', bootAll);
    var tries = 0;
    var t = setInterval(function () { if (bootAll() || ++tries > 50) clearInterval(t); }, 60);
  }
})();
